/**
 * The table's sound. One context, one buffer, fourteen slices.
 *
 * ============================== WHAT THIS IS ===================================
 * `sounds.ts` is the whole audio layer, and it is deliberately about a hundred lines of
 * Web Audio rather than a dependency. Howler is 30 KB to do what `AudioBufferSourceNode`
 * and one `GainNode` do here, and rule 6 says a runtime dependency needs a written
 * reason; "we would like to play a click" is not one. The FILES are assets, which is a
 * different thing from a dependency.
 *
 * ## The first-gesture rule, which is not a preference
 *
 * Every browser refuses to start an `AudioContext` outside a user gesture, and Safari
 * refuses to resume one it has suspended. So nothing here constructs a context at
 * import, at mount, or when a hand starts: `unlock()` is called from a click — the Play
 * card, the Deal button, the taunt tray — and it is the ONLY thing that constructs one.
 * Before that call, every `play()` returns without doing anything and without warning,
 * because a warning would appear on a page that is behaving correctly.
 * `tests/sounds.test.ts` asserts that importing this module constructs nothing.
 *
 * ## What the sprite is
 *
 * `public/sound/table.json` maps a cue id to `[offset ms, length ms]` into one decoded
 * buffer, and is written by `app/scripts/build-sound-sprite.mjs`. No offset is typed by
 * hand anywhere, so replacing a sound cannot desynchronise anything. The cues in it
 * today are synthesised placeholders and `public/sound/SOURCES.md` says so.
 *
 * ## Volume, mute, and reduced motion
 *
 * Volume and mute are the player's, from the Settings sheet, and are stored per device
 * by `settings/prefs.ts`. `prefers-reduced-motion` does NOT mute: a player who asked
 * for less movement did not ask for silence, and treating the two as the same setting
 * is how an accessibility preference becomes a punishment.
 * ===============================================================================
 */

/** Every cue the table can make. A missing one is a test failure, not a silent gap. */
export const CUES = [
  "shuffle", "deal", "flip", "chip-slide", "chip-stack", "pot-push", "check-knock",
  "fold", "timer-tick", "all-in", "win", "lose", "taunt-pop", "seat",
] as const;

export type Cue = (typeof CUES)[number];

/**
 * The Web Audio surface this module uses, declared structurally rather than imported.
 *
 * ============ WHY THIS IS NOT `lib.dom.d.ts` ==================================
 * `tsconfig.tests.json` type-checks the Worker (`src/**`) and the tests in one pass,
 * with `lib: ["ES2022"]` and `@cloudflare/workers-types` — no DOM, on purpose, because
 * the day a Worker route reaches for `document` the compiler should say so. This module
 * is imported by `tests/sounds.test.ts`, so it lands in that pass, and adding `DOM` to
 * the lib list to accommodate one file would take the guard off the Worker and invite a
 * clash between two sets of global `Request`/`Response` declarations besides.
 *
 * So the four types are written out. They are exactly the members used and nothing
 * else, which also makes the fake in the test a complete implementation of them rather
 * than a cast.
 * ==============================================================================
 */
interface Gain {
  gain: {
    value: number;
    setValueAtTime(v: number, t: number): void;
    linearRampToValueAtTime(v: number, t: number): void;
  };
  connect(to: unknown): void;
}
interface Source {
  buffer: unknown;
  connect(to: unknown): void;
  start(when: number, offset?: number, duration?: number): void;
  stop(when: number): void;
}
interface Ctx {
  destination: unknown;
  currentTime: number;
  createGain(): Gain;
  createBufferSource(): Source;
  decodeAudioData(bytes: ArrayBuffer): Promise<unknown>;
  resume?(): Promise<void>;
  close?(): Promise<void>;
}

export interface SpriteMap {
  version: number;
  sampleRate: number;
  totalMs: number;
  sprite: Record<string, [number, number]>;
}

const OGG = "/sound/table.ogg";
const M4A = "/sound/table.m4a";
const MAP = "/sound/table.json";

type Ctor = new () => Ctx;

interface Engine {
  ctx: Ctx;
  master: Gain;
  buffer: unknown;
  map: SpriteMap | null;
}

let engine: Engine | null = null;
let loading: Promise<void> | null = null;
let volume = 0.7;
let muted = false;

/** Has a gesture started the audio yet? The tests read this; so does the walk. */
export function isUnlocked(): boolean { return engine !== null; }

/** For the tests and the walk: what the module thinks the settings are. */
export function state(): { unlocked: boolean; muted: boolean; volume: number; loaded: boolean } {
  return { unlocked: engine !== null, muted, volume, loaded: !!engine?.buffer };
}

export function setVolume(v: number): void {
  volume = Math.max(0, Math.min(1, v));
  if (engine) engine.master.gain.value = muted ? 0 : volume;
}

export function setMuted(m: boolean): void {
  muted = m;
  if (engine) engine.master.gain.value = m ? 0 : volume;
}

/**
 * Start the audio. Call from a click and from nowhere else.
 *
 * Safe to call again: the second call resumes a context the browser suspended when the
 * tab went to the background, which is the other half of why this is a function rather
 * than a constructor.
 */
export function unlock(): void {
  const Ctx: Ctor | undefined =
    (globalThis as { AudioContext?: Ctor; webkitAudioContext?: Ctor }).AudioContext
    ?? (globalThis as { webkitAudioContext?: Ctor }).webkitAudioContext;
  if (!Ctx) return;
  if (!engine) {
    try {
      const ctx = new Ctx();
      const master = ctx.createGain();
      master.gain.value = muted ? 0 : volume;
      master.connect(ctx.destination);
      engine = { ctx, master, buffer: null, map: null };
    } catch {
      /* An unlock refused is a table without sound, not a table that is broken. */
      return;
    }
  }
  void engine.ctx.resume?.().catch(() => {});
  void load();
}

/** Fetch and decode the sprite, once. Never throws; a failure is silence. */
async function load(): Promise<void> {
  if (!engine || engine.buffer || loading) return loading ?? undefined;
  const e = engine;
  loading = (async () => {
    try {
      const map = (await (await fetch(MAP)).json()) as SpriteMap;
      /* Vorbis where it plays, AAC for Safari. `canPlayType` rather than sniffing the
         user agent, because the question is about the decoder and not about the brand. */
      /* `canPlayType` rather than sniffing the user agent, because the question is
         about the decoder and not about the brand. Structurally typed for the same
         reason the context is; under Node there is no `Audio` and OGG is assumed. */
      const A = (globalThis as { Audio?: new () => { canPlayType(t: string): string } }).Audio;
      const useOgg = !A || new A().canPlayType('audio/ogg; codecs="vorbis"') !== "";
      const res = await fetch(useOgg ? OGG : M4A);
      const bytes = await res.arrayBuffer();
      e.buffer = await e.ctx.decodeAudioData(bytes);
      e.map = map;
    } catch {
      /* Leave `buffer` null: `play()` is a no-op and the table is quiet. */
    }
    loading = null;
  })();
  return loading;
}

/**
 * Play one cue.
 *
 * A slice of the one buffer, gated through the master gain, with a two-millisecond
 * ramp at each end — without it every click starts and stops on a discontinuity and the
 * sprite crackles at exactly the moments the table is busiest.
 */
export function play(cue: Cue, opts: { gain?: number } = {}): void {
  /*
   * ============== THE ONE HOOK, AND WHY IT IS SAFE TO SHIP =====================
   * Playwright records video and NOT audio, so the only way to hand Tim a recording
   * with sound on it is to note when each cue fired and lay the sprite's slices onto
   * the video afterwards, by timestamp. That needs a list of cues and times, and this
   * is the whole of it.
   *
   * It costs one `Array.isArray` per cue and does nothing at all unless something has
   * already put an array at `globalThis.__bbCueLog` — which only `harness/_recordphase6.mjs`
   * ever does, before the app's own script runs. No flag, no build variant, no branch
   * that behaves differently in production: in production the array is not there and
   * the check is false, every time, forever.
   * ==========================================================================
   */
  const log = (globalThis as { __bbCueLog?: { cue: string; at: number }[] }).__bbCueLog;
  if (Array.isArray(log)) log.push({ cue, at: performance.now() });

  const e = engine;
  if (!e || !e.buffer || !e.map || muted || volume <= 0) return;
  const slice = e.map.sprite[cue];
  if (!slice) return;
  const [offMs, lenMs] = slice;
  try {
    const src = e.ctx.createBufferSource();
    src.buffer = e.buffer;
    const g = e.ctx.createGain();
    const now = e.ctx.currentTime;
    const peak = Math.max(0, Math.min(1, opts.gain ?? 1));
    const ramp = 0.002;
    const dur = lenMs / 1000;
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(peak, now + ramp);
    g.gain.setValueAtTime(peak, now + Math.max(ramp, dur - ramp));
    g.gain.linearRampToValueAtTime(0, now + dur);
    src.connect(g);
    g.connect(e.master);
    src.start(now, offMs / 1000, dur);
    src.stop(now + dur + 0.01);
  } catch {
    /* A source that will not start is one cue nobody hears. */
  }
}

/**
 * Everything the table shuts down when the route leaves.
 *
 * Not called on every unmount — the context is expensive and a player moving between
 * two tables should not pay for a second one — but the route calls it when the whole
 * game screen goes away, so a background tab is not holding an audio device open.
 */
export function close(): void {
  const e = engine;
  engine = null;
  loading = null;
  try { void e?.ctx.close?.(); } catch { /* already gone */ }
}

/* ------------------------------------------------------- steps to cues --- */

/**
 * Which cue a timeline step makes, if any.
 *
 * Pure, and separate from the playing, so `tests/sounds.test.ts` can assert that every
 * kind of step the Director can emit is either mapped or deliberately silent — a cue
 * that is missing because nobody noticed is the failure this function exists to make
 * impossible.
 *
 * The four cues NOT here are the four that are not steps: `win` and `lose` come from
 * the payoff (which is read off the felt, not off a step), `taunt-pop` comes from a
 * `Voice` event, and `seat` comes from the seat card letting the player in. Each is
 * played at its own call site and `tests/sounds.test.ts` checks all fourteen are used.
 */
export function cueForStep(step: { kind: string; action?: { kind: string }; chips?: boolean }): Cue | null {
  switch (step.kind) {
    case "shuffle": return "shuffle";
    case "deal": return "deal";
    case "post": return "chip-slide";
    case "action":
      switch (step.action?.kind) {
        case "fold": return "fold";
        case "check": return "check-knock";
        case "allin": return "all-in";
        default: return step.chips ? "chip-slide" : null;
      }
    case "collect": return "chip-stack";
    case "flip": return "flip";
    case "reveal": return "flip";
    case "push": return "pot-push";
    /* Deliberately silent: a beat, a hop, a clock, a hand ending. Silence is part of
       the rhythm — a table that makes a noise at every state change is a slot machine. */
    case "button": case "hole": case "think": case "await":
    case "pause": case "refund": case "end": case "hand_over":
      return null;
    default: return null;
  }
}
